import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "../context/globalContext";
import { useTheme } from "../hooks";
import { formatCurrency, getChargeRows } from "../utils";
import { toSurfaceTint } from "../utils/colorManager";

export type ReviewRow = { label: string; value: string };

// The last thing a payer reads before money moves: what they chose, then the
// server's quote. Charge and total come from the price call, never a sum here.
const ReviewSummary = ({
  rows,
  currency,
}: {
  rows: ReviewRow[];
  currency?: string;
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const {
    totalAmount,
    totalFees,
    instrumentFee,
    registrationFee,
    currency: paidCurrency,
  } = useGlobalContext();
  // The service being paid names the unit. The app-wide one is whatever an
  // earlier payment was made in and is never cleared, so it is only a fallback.
  const unit = currency || paidCurrency;

  const charges: ReviewRow[] = getChargeRows(
    instrumentFee,
    registrationFee,
    totalFees
  ).map(row => ({
    label: t(row.titleKey),
    value: formatCurrency(row.value, undefined, unit),
  }));

  const line = (row: ReviewRow, total = false) => (
    <Box
      key={row.label}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        px: 2,
        py: 1.25,
        borderTop: "1px solid",
        borderColor: "divider",
        "&:first-of-type": { borderTop: 0 },
        ...(total && { bgcolor: toSurfaceTint(theme.color_secondary) }),
      }}
    >
      <Typography
        component="dt"
        variant="body2"
        color={total ? "text.primary" : "text.secondary"}
        fontWeight={total ? 700 : 400}
      >
        {row.label}
      </Typography>
      <Typography
        component="dd"
        variant={total ? "h6" : "body2"}
        fontWeight={total ? 700 : 600}
        color={total ? theme.color_primary : "text.primary"}
        sx={{ m: 0, textAlign: "right", overflowWrap: "anywhere" }}
      >
        {row.value}
      </Typography>
    </Box>
  );

  return (
    <Box
      component="dl"
      aria-label={t("review.label")}
      sx={{
        m: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {[...rows, ...charges].map(row => line(row))}
      {line(
        {
          label: t("payment.form.total_amount"),
          value: formatCurrency(totalAmount, undefined, unit),
        },
        true
      )}
    </Box>
  );
};

export default ReviewSummary;
